import { type CSSProperties, forwardRef } from 'react';

import {
  Box,
  Checkbox,
  CheckboxProps,
  IconButton,
  Paper,
  Radio,
  RadioProps,
  Typography,
  alpha,
  styled,
} from '@mui/material';
import { DataGridPremium, DataGridPremiumProps } from '@mui/x-data-grid-premium';

import { blendWithWhite } from '@/shared/lib/colorBlend';

import { MAX_TREE_INDENT_DEPTH, TREE_INDENT_STEP_REM } from './GeneratikTable.constants';
import { AXIS_FIELD_ATTR, AXIS_HOVER_ATTR } from './timeline/axisHover';
import { MAX_AXIS_COLUMNS } from './timeline/bookingsAxis';
import { getPeriodFieldName } from './timeline/timeline.utils';

export const disabledCheckboxWrapperStyle: CSSProperties = {
  overflow: 'visible',
  display: 'inline-flex',
};

const ForwardedDataGrid = forwardRef<HTMLDivElement, DataGridPremiumProps>((props, ref) => (
  <DataGridPremium {...props} ref={ref} />
));
ForwardedDataGrid.displayName = 'ForwardedDataGrid';

export const StyledGeneratikDataGrid = styled(ForwardedDataGrid)(({ theme }) => {
  const accentMain = theme.palette.accent?.main || '#8E6DE1';

  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const headerBg =
    accentMain === theme.palette.blue?.[500]
      ? theme.palette.blue![100]
      : blendWithWhite(accentMain, 0.3);
  const childRowBg = blendWithWhite(accentMain, 0.15);
  const selectedPinnedCellBg = blendWithWhite(accentMain, 0.15);

  const treeIndentLevels = Object.fromEntries(
    Array.from({ length: MAX_TREE_INDENT_DEPTH }, (_, index) => [
      `&.GeneratikTable-treeDepth-${index + 1}`,
      { '--gt-tree-indent': `${(index + 1) * TREE_INDENT_STEP_REM}rem` },
    ]),
  );

  // A bookings-axis boundary highlights as one thing — its gridline AND the header date that
  // names it — from whichever end the pointer entered. AxisBoundaryHeader (the date) and
  // AxisGridlineHoverTarget (the strip straddling the line) both stamp AXIS_HOVER_ATTR on the grid
  // root: one field, or two space-separated fields for a merged label (one caption, two
  // boundaries). These rules carry that mark back to BOTH ends — down to each named column's
  // anchor ::before, and back up to whichever element declared AXIS_FIELD_ATTR for it. ~=
  // (whitespace-list match) is what lets a single attribute light both halves of a merged pair,
  // and equally what lets a single-field mark raised at one gridline find the merged label that
  // speaks for two. One rule per possible index because CSS cannot correlate the root's attribute
  // VALUE with a cell's data-field — and the hovered column's index is only known at runtime.
  // Enumerating (same pattern as treeIndentLevels above) keeps both ends pure CSS: rows
  // virtualizing in mid-hover match at mount, and nothing here can be wiped by MUI's own className
  // rewrites. Inclusive upper bound: a maximal axis's trailing column is index MAX_AXIS_COLUMNS
  // itself. The line takes the tenant accent and doubles to 2px; at the anchor's left: 0 the extra
  // pixel grows into the cell, which at this width is imperceptible.
  const axisHoverEmphasis = Object.fromEntries(
    Array.from({ length: MAX_AXIS_COLUMNS + 1 }, (_, index) => {
      const field = getPeriodFieldName(index);
      const marked = `&[${AXIS_HOVER_ATTR}~="${field}"]`;
      return [
        [
          `${marked} .MuiDataGrid-cell.timeline-axis-cell--anchor[data-field="${field}"]::before`,
          { backgroundColor: accentMain, width: '2px' },
        ],
        [`${marked} [${AXIS_FIELD_ATTR}~="${field}"]`, { color: accentMain }],
      ];
    }).flat(),
  );

  return {
    border: 'none',
    borderRadius: 0,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    fontFamily: 'Neue Frutiger World',

    '& .MuiDataGrid-main': {
      backgroundColor: theme.palette.background.paper,
    },

    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: `${headerBg}!important`,
      borderBottom: `1px solid ${theme.palette.grey[100]}`,
      minHeight: 'var(--gt-header-height, 45px) !important',
      maxHeight: 'var(--gt-header-height, 45px) !important',

      '& > .MuiDataGrid-row--borderBottom': {
        background: 'none',
      },

      // The filler after the last header cell is part of the grid's layout, so
      // painting it (instead of bleeding a pseudo-element past the row edge)
      // never inflates the scrollable width.
      '& .MuiDataGrid-filler': {
        backgroundColor: headerBg,
      },
    },

    // MUI clips the last header only when the grid scrolls horizontally; without
    // that clip the last resize separator pokes past the column edge and turns
    // into 16px of scrollable overflow in the no-scroll case.
    '& .MuiDataGrid-columnHeader--last': {
      overflow: 'hidden',
    },

    '& .MuiDataGrid-cellEmpty': {
      padding: '0!important',
    },

    '& .MuiDataGrid-cell.no-padding': {
      padding: '0!important',
    },

    '& .MuiDataGrid-cell[data-field="__tree_data_group__"]': {
      padding: '0!important',
      overflow: 'visible',
      borderRightColor: 'transparent !important',
      // A nested row's chevron reaches past this fixed-width column, into the indent the
      // next cell opened for it. When that neighbour is pinned it is opaque and sits at
      // MUI's pinned z-index of 30, so it would paint over the chevron. No-op otherwise:
      // an unpinned grouping cell is statically positioned, so z-index does not apply.
      zIndex: 31,

      ':hover': {
        backgroundColor: `${theme.palette.grey[100]} !important`,
      },

      '& img': {
        borderRadius: '100px',
      },

      '& .MuiChip-label, & span, & div, & p, & button': {
        overflow: 'visible',
        textOverflow: 'unset',
        whiteSpace: 'nowrap',
      },

      '& .MuiChip-root, & .MuiChip-label': {
        minWidth: '5rem',
        textAlign: 'center',
      },
    },

    '& .MuiDataGrid-cell[data-field="view"]': {
      padding: '0 !important',
      overflow: 'visible',
    },

    '& .MuiDataGrid-cell[data-field="serviceTypeIcon"]': {
      overflow: 'visible',
      padding: '0 1rem 0 1rem !important',
      minWidth: '76px !important',
      maxWidth: '76px !important',
      width: '76px !important',
    },

    '& .MuiDataGrid-cell[data-field="status"].MuiDataGrid-cell--editing': {
      boxShadow: 'none !important',
      backgroundColor: 'transparent !important',
      border: 'none !important',
      // padding: '0 0.75rem !important',
      '& .MuiSelect-select': {
        paddingLeft: '0 0.75rem !important',
      },
    },

    '& .MuiDataGrid-cell[data-field="timeline"].MuiDataGrid-cell--editing': {
      boxShadow: 'none !important',
      backgroundColor: 'transparent !important',
      border: 'none !important',
    },

    '& .MuiDataGrid-cell[data-field^="__period_"]': {
      position: 'relative',
      padding: '0 !important',
      borderRight: 'none !important',
      overflow: 'visible',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: '50%',
        width: '1px',
        backgroundColor: '#C2C3CE',
        transform: 'translateX(-50%)',
        pointerEvents: 'none',
        transition: 'background-color 0.15s ease',
      },
    },

    '& .MuiDataGrid-columnHeader[data-field^="__period_"]': {
      padding: '0 0.25rem',
      justifyContent: 'center',
    },

    '& .MuiDataGrid-cell[data-field="meteo"], & .MuiDataGrid-columnHeader[data-field="meteo"]': {
      padding: '0 0.75rem 0 0 !important',
    },

    '& .MuiDataGrid-cell[data-field="meteo"] > *': {
      justifyContent: 'flex-start !important',
    },

    '& .MuiDataGrid-columnHeader[data-field="serviceTypeIcon"]': {
      padding: '0 1rem !important',
      minWidth: '76px !important',
      maxWidth: '76px !important',
      width: '76px !important',
    },

    '& .MuiDataGrid-columnHeaderRow': {
      backgroundColor: 'transparent',
    },

    '& .MuiDataGrid-columnHeader': {
      backgroundColor: `${headerBg}!important`,
      padding: '0 0.75rem',
      outline: 'none !important',
      height: 'var(--gt-header-height, 45px) !important',

      '&:focus, &:focus-within': {
        outline: 'none !important',
      },
    },

    '& .MuiDataGrid-columnHeader--dragging': {
      cursor: 'grabbing !important',
      backgroundColor: `${hexToRgba(accentMain, 0.55)}!important`,
      boxShadow: `0 8px 28px ${hexToRgba(accentMain, 0.55)}, 0 2px 8px ${hexToRgba(accentMain, 0.35)}`,
      borderRadius: 4,
      opacity: '0.75 !important',
      zIndex: 10,
    },

    '& .MuiDataGrid-cell.GeneratikTable-draggedColumnCell': {
      backgroundColor: `${hexToRgba(accentMain, 0.08)} !important`,
      boxShadow: `inset 3px 0 10px -2px ${hexToRgba(accentMain, 0.35)}, inset -3px 0 10px -2px ${hexToRgba(accentMain, 0.35)} !important`,
    },

    '@keyframes GT-filterableFlash': {
      '0%': { boxShadow: 'inset 0 0 0 9999px transparent' },
      '25%': { boxShadow: `inset 0 0 0 9999px ${hexToRgba(accentMain, 0.18)}` },
      '75%': { boxShadow: `inset 0 0 0 9999px ${hexToRgba(accentMain, 0.18)}` },
      '100%': { boxShadow: 'inset 0 0 0 9999px transparent' },
    },

    '& .MuiDataGrid-cell.GT-filterableFlash': {
      animation: 'GT-filterableFlash 1.2s ease-in-out',
    },

    '& .MuiDataGrid-cell.GT-filterable': {
      transition: 'box-shadow 0.5s ease-in-out',
      boxShadow: 'inset 0 0 0 9999px transparent',
    },

    '& .MuiDataGrid-cell.GT-filterableHighlight': {
      boxShadow: `inset 0 0 0 9999px ${hexToRgba(accentMain, 0.18)}`,
    },

    '& .timeline-period-header': {
      userSelect: 'none',
      pointerEvents: 'none',
    },
    '& .timeline-axis-header': {
      position: 'relative',
      // Boundary-centered labels overhang their own cell by half their width in both directions.
      // Interior header cells default to overflow: visible, but the last column's cell is
      // clipped — which cut the trailing label to its right-edge fragment. The plain declaration
      // below covers the (0,2,0) '--last' rules, but MUI's baseline also ships
      // '.root .virtualScroller--hasScrollX .columnHeader--last { overflow: hidden }' at
      // (0,3,0) — always active on a scrolling rail — so the compound variant rule after it
      // (same (0,3,0), declared later, scoped to axis headers only) is what actually wins.
      // Non-axis grids keep their last-column clip untouched.
      overflow: 'visible',
      '&.MuiDataGrid-columnHeader--last': {
        overflow: 'visible',
      },
      '& .MuiDataGrid-columnHeaderTitleContainer': {
        position: 'static',
        overflow: 'visible',
      },
      '& .MuiDataGrid-columnHeaderTitleContainerContent': {
        overflow: 'visible',
      },
    },
    // The calendar convention's divider is center-anchored (period start = column center); on the
    // bookings axis boundaries sit at column EDGES, so the same row gridline moves to the left
    // edge, aligning with the boundary-centered header dates. This gridline is the boundary's
    // ONLY vertical line since Round 19 — the header itself draws no tick — so it starts at the
    // first data row, right under the date.
    // Must OUTRANK the base '[data-field^="__period_"]::before' rule, not tie with it: that rule
    // is (0,3,1) — cell class + attribute + pseudo — so a bare '.timeline-axis-cell::before'
    // (0,2,1) silently loses `left` and the line renders at the calendar's 50%, half a column
    // away from the bar caps. Repeating the cell class and attribute lifts this to (0,4,1).
    '& .MuiDataGrid-cell.timeline-axis-cell--anchor[data-field^="__period_"]::before': {
      left: 0,
      transform: 'none',
    },
    // v5 anchors-only: only an anchor-kind column (the hook's own timeline-axis-cell--anchor
    // modifier — booking/period/window edges, plus the trailing column) keeps this gridline; a
    // mid-booking tick draws no header tick either, so a gridline under it would dangle with
    // nothing above to anchor it to. A break carries neither modifier despite its own
    // startKind being 'anchor' on the axis model — its torn edges mark it instead, and the
    // anchor that resumes right after it supplies the resume line. Same specificity as the base
    // '.MuiDataGrid-cell[data-field^="__period_"]::before' rule (two class-level selectors plus
    // the pseudo-element each) — :not()'s specificity is that of its own argument — so this
    // wins on `display` purely because it is the only rule that sets it at all; being defined
    // later only matters as the tiebreaker CSS would use if that weren't already true.
    '& .timeline-axis-cell:not(.timeline-axis-cell--anchor)::before': {
      display: 'none',
    },
    // A break cell draws its compressed-stretch band over its whole area, and that band is a
    // later sibling of this pseudo-element in the same auto z-layer — so without a lift the
    // boundary line under a gap's date would paint UNDERNEATH the band's tint and hatch. One
    // positive z-index puts it back on top; the base rule's pointer-events: none keeps it from
    // stealing the band's own tooltip. Only break cells need this: everywhere else the line is
    // meant to sit under the bars it measures.
    '& .MuiDataGrid-cell.timeline-axis-cell--break[data-field^="__period_"]::before': {
      zIndex: 1,
    },
    // Both ends of the boundary emphasis fade rather than snap, matching the gridline's own
    // transition on the base '__period_' cell rule above.
    [`& [${AXIS_FIELD_ATTR}]`]: {
      transition: 'color 0.15s ease',
    },
    // See axisHoverEmphasis above — a hovered boundary lights its own anchor gridline and the
    // header date naming it, whichever end raised the mark. Specificity (0,5,1) at the gridline
    // end: the root attribute selector adds one level over the (0,4,1) anchor edge rule, so
    // backgroundColor/width win there without touching that rule's left/transform. The date end is
    // (0,3,0), which clears the label's own (0,1,0) emotion class from AXIS_LABEL_SX.
    ...axisHoverEmphasis,
    // A break/gap column's header gets the same light tint as its cells (TimelinePeriodCell's
    // full-height band), so the "this is a cut in time" treatment reads as one continuous band
    // from header to row rather than stopping short at the column boundary.
    '& .timeline-axis-gap-header': {
      backgroundColor: `${alpha(theme.palette.grey[400], 0.12)} !important`,
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontFamily: 'Neue Frutiger World',
      fontSize: 'var(--gt-font-size, 0.875rem)',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#615E6F',
      textTransform: 'none',
      letterSpacing: 0,
    },

    '&& .MuiDataGrid-columnSeparator:not(.MuiDataGrid-columnSeparator--resizable)': {
      display: 'none',
    },

    '&& .MuiDataGrid-columnSeparator--resizable': {
      color: '#615E6F',
      opacity: 0,
      transition: 'opacity 0.2s ease, color 0.2s ease',
    },

    '&& .MuiDataGrid-iconSeparator': {
      height: '1.5rem',
    },

    '&& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator--resizable, && .MuiDataGrid-columnSeparator--resizable:hover, && .MuiDataGrid-columnSeparator--resizing':
      {
        opacity: 1,
      },

    '&& .MuiDataGrid-columnSeparator--resizable:hover, && .MuiDataGrid-columnSeparator--resizing': {
      color: accentMain,
    },

    '& .MuiDataGrid-sortIcon': {
      color: '#615E6F',
      fontSize: '0.875rem',
      opacity: 0,
      transition: 'opacity 0.2s ease',
    },

    '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-sortIcon': {
      opacity: 1,
    },

    '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon': {
      opacity: 1,
    },

    // Multi-column sort priority. The grid only renders this badge once more
    // than one column is sorted, so a single sort still shows a bare arrow.
    '& .MuiDataGrid-iconButtonContainer .MuiBadge-badge': {
      minWidth: '0.875rem',
      height: '0.875rem',
      padding: '0 0.1875rem',
      backgroundColor: accentMain,
      color: '#FFFFFF',
      fontSize: '0.625rem',
      fontWeight: 600,
      lineHeight: 1,
      transform: 'scale(1) translate(70%, -50%)',
    },

    '& .MuiDataGrid-menuIcon': {
      marginLeft: '0.25rem',
      visibility: 'hidden',
    },

    '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer':
      {
        flexDirection: 'row !important',
      },

    '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-menuIcon': {
      marginLeft: '0.25rem !important',
      marginRight: '0 !important',
    },

    '& .MuiDataGrid-treeDataGroupingCellToggle': {
      marginRight: 0,
    },

    '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': {
      visibility: 'visible',
    },

    '& .MuiDataGrid-cell': {
      fontFamily: 'Neue Frutiger World',
      fontSize: 'var(--gt-font-size, 0.875rem)',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#615E6F',
      padding: '0 0.75rem',
      borderBottom: `1px solid ${theme.palette.grey[100]}`,
      borderTop: 'none',
      display: 'flex',
      alignItems: 'center',
      outline: 'none !important',
      minHeight: 'var(--gt-row-height, 55px) !important',
      maxHeight: 'var(--gt-row-height, 55px) !important',

      '&:focus, &:focus-within': {
        outline: 'none !important',
      },

      '& > span': {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      },

      '& .MuiTypography-root': {
        fontSize: 'inherit',
        fontWeight: 'inherit',
        color: 'inherit',
        fontFamily: 'inherit',
        lineHeight: 'inherit',
        letterSpacing: 'inherit',
      },

      '&.clickable-cell': {
        cursor: 'pointer',
      },

      '&.GeneratikTable-treeIndentCell': {
        paddingLeft: 'calc(0.75rem + var(--gt-tree-indent, 0rem))',
      },
    },

    '& .MuiDataGrid-row': {
      minHeight: 'var(--gt-row-height, 55px) !important',
      maxHeight: 'var(--gt-row-height, 55px) !important',
      backgroundColor: '#FEFEFE',

      ...treeIndentLevels,

      '&:hover': {
        backgroundColor: `${theme.palette.grey[100]} !important`,
      },

      '&.Mui-selected': {
        backgroundColor: `${hexToRgba(accentMain, 0.15)} !important`,

        '&:hover': {
          backgroundColor: `${hexToRgba(accentMain, 0.15)} !important`,
        },
      },

      '&.GeneratikTable-treeParentRow': {
        '& .MuiCheckbox-indeterminate': {
          color: theme.palette.action.active,
        },
      },

      '&.GeneratikTable-groupRow': {
        minHeight: 'var(--gt-header-height, 45px) !important',
        maxHeight: 'var(--gt-header-height, 45px) !important',

        '& .MuiDataGrid-cell': {
          minHeight: 'var(--gt-header-height, 45px) !important',
          maxHeight: 'var(--gt-header-height, 45px) !important',
        },

        '& .MuiDataGrid-cell[data-field="__tree_data_group__"]': {
          overflow: 'visible',
          zIndex: 9999,

          '& img': {
            borderRadius: '100px',
          },

          '& .MuiChip-label, & span, & div, & p, & button': {
            overflow: 'visible',
            textOverflow: 'unset',
            whiteSpace: 'nowrap',
          },

          '& .MuiChip-root, & .MuiChip-label': {
            minWidth: '5rem',
            textAlign: 'center',
          },
        },

        '& .MuiDataGrid-cell:not([data-field="__tree_data_group__"]):not(.MuiDataGrid-cellCheckbox)':
          {
            '& > *': {
              visibility: 'hidden',
            },
          },

        '& .MuiCheckbox-indeterminate': {
          color: theme.palette.action.active,
        },

        '& .MuiDataGrid-cell--withRightBorder': {
          borderRightColor: 'transparent!important',
        },
      },

      '&.GeneratikTable-groupRow-expanded': {
        backgroundColor: `var(--gt-group-row-bg, ${theme.palette.purple?.[50] ?? blendWithWhite(accentMain, 0.08)}) !important`,

        '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
          backgroundColor: `var(--gt-group-row-bg, ${theme.palette.purple?.[50] ?? blendWithWhite(accentMain, 0.08)}) !important`,
        },

        '&:hover': {
          backgroundColor: `var(--gt-group-row-bg-hover, ${theme.palette.purple?.[100] ?? blendWithWhite(accentMain, 0.12)}) !important`,

          '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
            backgroundColor: `var(--gt-group-row-bg-hover, ${theme.palette.purple?.[100] ?? blendWithWhite(accentMain, 0.12)}) !important`,
          },
        },

        '&.Mui-selected': {
          backgroundColor: `${hexToRgba(accentMain, 0.15)} !important`,

          '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
            backgroundColor: `${selectedPinnedCellBg} !important`,
          },

          '&:hover': {
            backgroundColor: `${hexToRgba(accentMain, 0.15)} !important`,

            '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
              backgroundColor: `${selectedPinnedCellBg} !important`,
            },
          },
        },
      },

      '&.GeneratikTable-childRow': {
        backgroundColor: childRowBg,

        '&:hover': {
          backgroundColor: `${blendWithWhite(accentMain, 0.2)} !important`,
        },

        '&.Mui-selected': {
          backgroundColor: `${hexToRgba(accentMain, 0.25)} !important`,

          '&:hover': {
            backgroundColor: `${hexToRgba(accentMain, 0.25)} !important`,
          },
        },
      },
    },

    '& .MuiDataGrid-groupingCriteriaCell': {
      fontWeight: 600,
      color: '#504E5A',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',

      '& span:nth-child(2)': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },

    '& .MuiDataGrid-groupingCriteriaCellToggle': {
      alignItems: 'center',
      marginRight: 0,
      display: 'flex',

      '& .MuiIconButton-root': {
        padding: '0.25rem',
        color: accentMain,

        '&:hover': {
          backgroundColor: hexToRgba(accentMain, 0.1),
        },
      },
    },

    '& .MuiDataGrid-columnHeaderTitleContainerContent': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    '& .MuiDataGrid-cellCheckbox': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'visible',

      '& .MuiCheckbox-root': {
        overflow: 'visible',
      },
    },

    '& .MuiDataGrid-pinnedColumnHeaders--left': {
      overflow: 'visible !important',
    },

    '& .MuiDataGrid-columnHeader--checkbox, & .MuiDataGrid-columnHeaderCheckbox': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'visible !important',

      '& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainerContent':
        {
          overflow: 'visible !important',
        },

      '& > div, & > span': {
        overflow: 'visible !important',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },

      '& .MuiCheckbox-root': {
        overflow: 'visible !important',
      },
    },

    '& .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedRight, & .MuiDataGrid-cell--pinnedRight':
      {
        backgroundColor: '#FEFEFE',
        boxShadow: 'none',
      },

    '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight':
      {
        backgroundColor: `${theme.palette.grey[100]} !important`,
      },

    '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight':
      {
        backgroundColor: `${selectedPinnedCellBg} !important`,
      },

    '& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight':
      {
        backgroundColor: `${selectedPinnedCellBg} !important`,
      },

    '& .MuiDataGrid-row.GeneratikTable-childRow .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.GeneratikTable-childRow .MuiDataGrid-cell--pinnedRight':
      {
        backgroundColor: childRowBg,
      },

    '& .MuiDataGrid-row.GeneratikTable-childRow:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.GeneratikTable-childRow:hover .MuiDataGrid-cell--pinnedRight':
      {
        backgroundColor: `${blendWithWhite(accentMain, 0.2)} !important`,
      },

    '& .MuiDataGrid-row.GeneratikTable-childRow.Mui-selected .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.GeneratikTable-childRow.Mui-selected .MuiDataGrid-cell--pinnedRight':
      {
        backgroundColor: `${blendWithWhite(accentMain, 0.25)} !important`,
      },

    '& .MuiDataGrid-row.GeneratikTable-childRow.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.GeneratikTable-childRow.Mui-selected:hover .MuiDataGrid-cell--pinnedRight':
      {
        backgroundColor: `${blendWithWhite(accentMain, 0.25)} !important`,
      },

    '& .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedRight': {
      backgroundColor: `${headerBg}!important`,
      zIndex: 9999,
    },

    '& .MuiDataGrid-filler--pinnedLeft, & .MuiDataGrid-filler--pinnedRight': {
      backgroundColor: theme.palette.background.paper,
      borderLeftColor: 'transparent!important',
      borderRightColor: 'transparent!important',
    },

    '& .MuiDataGrid-footerContainer': {
      display: 'none',
    },

    '& .MuiDataGrid-overlay': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },

    '& .MuiDataGrid-overlayWrapper': {
      minHeight: '6.25rem',
    },

    '& .MuiDataGrid-toolbarContainer': {
      padding: '0.75rem 1rem',
      gap: '0.5rem',
      borderBottom: `1px solid ${theme.palette.grey[100]}`,

      '& .MuiButton-root': {
        fontFamily: 'Neue Frutiger World',
        fontSize: '0.75rem',
        fontWeight: 500,
        textTransform: 'none',
      },
    },

    '& .MuiDataGrid-virtualScroller': {
      backgroundColor: theme.palette.background.paper,
    },

    // A detail panel annotates one row, so its content sticks to the left of the scrollport rather
    // than sliding off behind the columns. MUI ships the panel as its own scroll container, which
    // would anchor that sticky content to the panel instead of to the grid — so it must not be one.
    '& .MuiDataGrid-detailPanel': {
      overflow: 'visible',
    },

    '& .MuiDataGrid-scrollbar--horizontal': {
      display: 'none',
    },

    '& .MuiDataGrid-scrollbar--vertical': {
      display: 'none',
    },

    '& .MuiDataGrid-cell--skeleton': {
      backgroundColor: theme.palette.grey[100],
    },
  };
});

export { StatusChip } from '@/shared/ui/cells/cells.styles';

export const TableContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fillHeight',
})<{ fillHeight?: boolean }>(({ fillHeight }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: fillHeight ? '100%' : 'auto',
  flex: fillHeight ? 1 : 'none',
  minHeight: fillHeight ? 0 : 'auto',
}));

export const ToolbarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.75rem 1rem',
  borderBottom: `1px solid ${theme.palette.grey[100]}`,
  backgroundColor: theme.palette.background.paper,
  borderTopLeftRadius: '0.5rem',
  borderTopRightRadius: '0.5rem',
}));

export const ToolbarActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
});

export const PaginationFooter = styled(Box)<{ isModal?: boolean }>(({ theme, isModal }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: isModal ? 0 : '0.625rem',
  backgroundColor: theme.palette.background.paper,
  minHeight: isModal ? 0 : '3.0625rem',
  flex: 1,
}));

export const PaginationLeftSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.625rem',
});

export const PaginationRightSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.625rem',
});

export const PaginationPill = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: '1.25rem',
  padding: '1rem 0.75rem',
  borderRadius: '0.625rem',
  backgroundColor: theme.palette.grey[50],
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.75rem',
  lineHeight: 1.6,
  gap: '0.312rem',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',

  '&:hover': {
    backgroundColor: '#F9F9F9',
  },
}));

export const PaginationLabel = styled('span')(({ theme }) => ({
  color: theme.palette.grey[800],
  fontWeight: 400,
}));

export const PaginationValue = styled('span')(({ theme }) => ({
  color: theme.palette.grey[950],
  fontWeight: 600,
}));

export const PaginationDivider = styled(Box)({
  width: '1px',
  height: '0.75rem',
  backgroundColor: '#DBDBE2',
  borderRadius: '3.625rem',
  margin: '0 0.25rem',
});

export const PaginationIconButton = styled(IconButton)({
  padding: 0,
  width: '1.25rem',
  height: '1.25rem',
  color: '#807D93',

  '&:hover': {
    backgroundColor: 'transparent',
    color: '#504E5A',
  },

  '&.Mui-disabled': {
    color: '#DBDBE2',
  },

  '& .MuiSvgIcon-root': {
    fontSize: '1.25rem',
  },
});

export const CellWithLabel = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: '0.125rem',
  overflow: 'hidden',
});

export const CellPrimaryText = styled('span')({
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.5,
  color: '#615E6F',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  maxWidth: '100%',
});

export const CellSecondaryText = styled('span')({
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.625rem',
  fontWeight: 400,
  lineHeight: 1.6,
  color: '#807D93',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  maxWidth: '100%',
});

export const ProgressBarCell = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '0.375rem',
});

export const ProgressBarTrack = styled(Box)({
  width: '100%',
  height: '0.5rem',
  borderRadius: '0.25rem',
  backgroundColor: '#E8E8EC',
  overflow: 'hidden',
});

export const ProgressBarFill = styled(Box)<{ value: number; barcolor?: string }>(
  ({ value, barcolor }) => ({
    height: '100%',
    width: `${Math.min(100, Math.max(0, value))}%`,
    borderRadius: '0.25rem',
    backgroundColor: barcolor || '#F59E0B',
    transition: 'width 0.3s ease',
  }),
);

export const ProgressBarLabel = styled('span')({
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.625rem',
  fontWeight: 400,
  lineHeight: 1.6,
  color: '#807D93',
  textAlign: 'right',
});

export const TablePaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'noPaper' && prop !== 'seamless',
})<{ noPaper?: boolean; seamless?: boolean }>(({ noPaper, seamless }) => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  borderRadius: noPaper ? 0 : seamless ? '0 0 0.875rem 0.875rem' : '0.875rem',
  boxShadow: noPaper || seamless ? 'none' : '0 1px 8px 0 rgba(0, 0, 0, 0.15)',
  overflow: noPaper ? 'visible' : 'hidden',
  background: noPaper ? 'transparent' : undefined,
}));

export const DataGridWrapper = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
});

export const NoRowsOverlayContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: '#807D93',
});

export const NoRowsText = styled(Typography)({
  fontFamily: 'Neue Frutiger World',
});

/**
 * Deliberately distinct from `NoRowsOverlayContainer`: a failed fetch must never
 * be mistaken for an empty account, so the error overlay stacks vertically, uses
 * the theme error colour and leaves room for a retry affordance.
 */
export const ErrorOverlayContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  height: '100%',
  padding: '1rem',
  textAlign: 'center',
  color: theme.palette.error.main,
}));

export const ErrorOverlayText = styled(Typography)({
  fontFamily: 'Neue Frutiger World',
});

export const SelectedRowsText = styled(Typography)({
  color: '#807D93',
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.75rem',
  flexShrink: 0,
});

export const StyledCheckbox = styled((props: CheckboxProps) => (
  <Checkbox disableRipple {...props} />
))(({ theme }) => {
  const accentMain = theme.palette.accent?.main || '#8E6DE1';

  return {
    padding: 0,
    border: 'none',
    borderRadius: '0.25rem',

    fontSize: '1rem',
    width: '1rem',
    height: '1rem',
    backgroundColor: '#FEFEFE',
    '& .MuiSvgIcon-root': {
      overflow: 'hidden',
    },

    '&:not(.Mui-checked):not(.MuiCheckbox-indeterminate)': {
      // color: 'rgba(103, 110, 118, 0.25)',
      borderRadius: '0.25rem',
      color: '#FEFEFE',
      boxShadow:
        '0 3px 5px 0 rgba(103, 110, 118, 0.08), 0 0 0 2px rgba(103, 110, 118, 0.16), 0 1px 1px 0 rgba(0, 0, 0, 0.12)',
    },

    '&.Mui-checked': {
      color: accentMain,
      boxShadow: `0 2px 5px 0 ${alpha(theme.palette.accent.main, 0.28)}, 0 0 0 1px 
      ${alpha(theme.palette.accent.main, 0.16)}, 0 1px 1px 0 ${alpha(theme.palette.accent.main, 0.12)}`,
    },

    '&.MuiCheckbox-indeterminate': {
      color: accentMain,
      boxShadow: `0 2px 5px 0 ${alpha(theme.palette.accent.main, 0.28)}, 0 0 0 1px 
      ${alpha(theme.palette.accent.main, 0.16)}, 0 1px 1px 0 ${alpha(theme.palette.accent.main, 0.12)}`,
    },
  };
});

export const StyledRadio = styled((props: RadioProps) => <Radio disableRipple {...props} />)(({
  theme,
}) => {
  const accentMain = theme.palette.accent?.main || '#8E6DE1';

  return {
    padding: 0,
    border: 'none',
    fontSize: '1rem',
    width: '1rem',
    height: '1rem',
    backgroundColor: '#FEFEFE',

    '&:not(.Mui-checked)': {
      color: '#B0AEC0',
      boxShadow:
        '0 3px 5px 0 rgba(103, 110, 118, 0.08), 0 0 0 2px rgba(103, 110, 118, 0.16), 0 1px 1px 0 rgba(0, 0, 0, 0.12)',
    },

    '&.Mui-checked': {
      color: accentMain,
      boxShadow: `0 2px 5px 0 ${alpha(theme.palette.accent.main, 0.28)}, 0 0 0 1px ${alpha(theme.palette.accent.main, 0.16)}, 0 1px 1px 0 ${alpha(theme.palette.accent.main, 0.12)}`,
    },
  };
});

export const FiltersTitle = styled('p')(({ theme }) => ({
  margin: 0,
  color: theme.palette.secondary.dark,
  fontSize: '1.125rem',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '120%',
}));
